import _ from 'lodash'
import delay from 'delay'
import Guard from 'arg-guard'
import moment from 'moment'

import Config from '../app.config'
import Logger from '../logger'

import Store from '@/store'
import { MUTATIONS } from '@/store/mutations'
import AppError from '@/models/app-error'

import ServiceFactory from '@/services/service-factory'
import ServiceOperation from '@/services/service-operation'

const loggingCategory = 'Polling'

/**
 * Class for Status Polling.
 *
 * @export
 * @class Poller
 */
export default class StatusPoller {

    /**
     * Creates an instance of StatusPoller.
     * @param {string} operation The name of the operation.
     * @param {number} id The id for the data to poll.
     * @param {string} pollFunctionName The function name responsible for polling.
     * @memberof StatusPoller
     */
    constructor(operation, id, pollFunctionName) {
        Guard.throwIf(operation, 'operation')
        Guard.throwIf(id, 'id')
        Guard.throwIf(pollFunctionName, 'pollFunctionName')

        this.operationName = operation
        this.pollId = id
        this.pollFunctionName = pollFunctionName
    }

    /**
     * Checks the status API once only.
     *
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof StatusPoller
     */
    async check() {
        try {
            const service = ServiceFactory.status()
            Logger.logObject({ time: moment().format('hh:mm:ss') }, `Checking status for (${ this.operationName })`, loggingCategory)
            return await this._poller(service, this.pollId, { runOnce: true })
        } catch (err) {
            Logger.log(`Unable to check status: ${ err.message }`, loggingCategory)
            throw err
        }
    }

    /**
     * Polls the status API until an operation completes.
     *
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof StatusPoller
     */
    async poll() {
        try {
            const service = ServiceFactory.status()
            Logger.logObject({ time: moment().format('hh:mm:ss') }, `Operation started (${ this.operationName })`, loggingCategory)
            return await this._poller(service, this.pollId)
        } catch (err) {
            Logger.log(`Unable to poll for status: ${ err.message }`, loggingCategory)
            throw err
        }
    }

    /**
     * Polling routine to check the state.
     *
     * @param {Service} service The status service.
     * @param {number} id The ID to check for.
     * @param {Object} { attemptsRemaining = null, runOnce = false }
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof StatusPoller
     */
    async _poller(service, id, { attemptsRemaining = null, runOnce = false } = {}) {
        const dataOperation = new ServiceOperation(this.operationName, true)
        if (!_.isFunction(service[this.pollFunctionName]))
            throw new TypeError(`The function name '${ this.pollFunctionName } is not a valid function on the service.`)

        const time = moment().format('hh:mm:ss')
        Logger.logObject({ id, function: this.pollFunctionName, time, attemptsRemaining, runOnce }, `Attempt for ${ this.operationName }`, loggingCategory)

        try {
            const operationState = await service[this.pollFunctionName](id)
            if (operationState) dataOperation.updateMergeFrom(operationState)

            if (operationState.data && operationState.data.statusCode !== 0 && operationState.data.statusCode !== 2) {
                Logger.logObject({ id, status: operationState.data.message, time },
                    `${ this.operationName } for ID ${ id } is at status '${ operationState.data.message }'`, loggingCategory)

                const runPoll = async(maxAttempts, tooLateError) => {
                    const remains = (attemptsRemaining && attemptsRemaining != null ? attemptsRemaining : maxAttempts) - 1
                    if (remains > 0) {
                        await delay(Config.apiPolling.interval * 1000)
                        return await this._poller(service, id, { attemptsRemaining: remains })
                    }
                    Logger.error(tooLateError, loggingCategory)
                    Store.commit(MUTATIONS.logAppError, new AppError(tooLateError, true))
                    dataOperation.complete(false, tooLateError)
                    return dataOperation
                }

                if (runOnce) {
                    dataOperation.stillPending = true
                    return dataOperation
                }

                if (operationState.data.statusCode === -1)
                    return await runPoll(Config.apiPolling.attempts, `Unable to start ${ this.operationName }`)


                return await runPoll(Config.apiPolling.attempts, `${ this.operationName } did not complete within the allotted time of ${ Config.apiPolling.interval * Config.apiPolling.attempts } seconds.`)
            } else if (operationState.data && operationState.data.statusCode === 0) {
                Logger.logObject({ id, status: operationState.data.message, time }, `${ this.operationName } for ID ${ id } completed`, loggingCategory)
                return dataOperation
            }

            const errMsg = operationState.data.message.includes(`Address ${ id } has no data`)
                ? `Address ${ id } has no data`
                : operationState.data.message.includes('RdSAPBot Message')
                    ? `Address ${ id } has bad data`
                    : `${ this.operationName } for ID ${ id } failed`

            Logger.error(errMsg, loggingCategory)
            Store.commit(MUTATIONS.logAppError, new AppError(errMsg, true))
            return dataOperation

        } catch (err) {
            Logger.logObject(dataOperation, `Fail state on ${ this.operationName }`, loggingCategory)
            dataOperation.fail(err)
            throw err
        }
    }

    /**
     * Executes a data function after a check using a polling routine.
     *
     * @param {string} operationName The name of the operation.
     * @param {string} pollFunctionName The function name responsible for polling.
     * @param {Function} onComplete The function to execute after the poll completes.
     * @param {number} id The id for the data to poll.
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof StatusPoller
     */
    static async executeAfterCheckAsync(operationName, pollFunctionName, onComplete, id) {
        Guard.throwIf(operationName, 'operationName')
        Guard.throwIf(pollFunctionName, 'pollFunctionName')
        Guard.throwIf(onComplete, 'onComplete')
        Guard.throwIf(id, 'id')

        const dataOperation = new ServiceOperation(`Checking (${ operationName })`, true)

        try {
            const poller = new StatusPoller(operationName, id, pollFunctionName)
            const operation = await poller.check()
            if (operation) dataOperation.updateMergeFrom(operation, true)
        } catch (err) {
            try {
                await onComplete()
                Logger.warn(`Operation completed despite an error while checking - ${ err.message }`, loggingCategory)
                return dataOperation
            } catch (err2) {
                dataOperation.fail(err2)
                throw err2
            }
        }

        try {
            await onComplete()
            if (dataOperation.stillPending)
                Logger.warn('Operation completed despite pending service operations when checked', loggingCategory)
            return dataOperation
        } catch (err) {
            dataOperation.fail(err)
            throw err
        }
    }

    /**
     * Executes a data function after waiting for a polling routine.
     *
     * @param {string} operationName The name of the operation.
     * @param {string} pollFunctionName The function name responsible for polling.
     * @param {Function} onComplete The function to execute after the poll completes.
     * @param {number} id The id for the data to poll.
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof StatusPoller
     */
    static async executeAfterPollAsync(operationName, pollFunctionName, onComplete, id) {
        Guard.throwIf(operationName, 'operationName')
        Guard.throwIf(pollFunctionName, 'pollFunctionName')
        Guard.throwIf(onComplete, 'onComplete')
        Guard.throwIf(id, 'id')

        const dataOperation = new ServiceOperation(`Polling (${ operationName })`, true)

        try {
            const poller = new StatusPoller(operationName, id, pollFunctionName)
            const operation = await poller.poll()
            if (operation) dataOperation.updateMergeFrom(operation, true)
        } catch (err) {
            try {
                await onComplete()
                Logger.warn(`Operation completed despite an error while polling - ${ err.message }`, loggingCategory)
                return dataOperation
            } catch (err2) {
                dataOperation.fail(err2)
                throw err2
            }
        }

        try {
            await onComplete()
            if (dataOperation.stillPending)
                Logger.warn('Operation completed despite pending service operations', loggingCategory)
            return dataOperation
        } catch (err) {
            dataOperation.fail(err)
            throw err
        }
    }
}
